import { InfoPage } from '@/components/info-page';
import { EntityTable } from '@/components/table/entity-table';
import { TableErrorState } from '@/components/table/table-error-state';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown';
import { TableFilterInputField } from '@/components/table/table-filter-field';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button';
import { Button } from '@/components/ui/button';
import { getAssessmentContents } from '@/lib/api-calls';
import { useErrorToast } from '@/lib/error-handling';
import { useDebouncedSearchState, validateRouteRole } from '@/lib/routing';
import { CreateAssessmentContentsByCsv } from '@/routes/_layout/lessons/assessment-contents/-components/create-assessment-contents-by-csv';
import { isAirAdmin } from '@airreading/common';
import { AssessmentContent, AssessmentFormat, assessmentFormatSchema } from '@airreading/common/data';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useCallback, useMemo } from 'react';
import { z } from 'zod';
import { lessonsSectionTabs } from '../-components/tabs';

const assessmentContentsSearchSchema = z.object({
  query: z.string().optional().catch(''),
  formats: z.array(assessmentFormatSchema).optional(),
});

const assessmentFormatFilterOptions = Object.values(AssessmentFormat).map((format) => ({
  label: format,
  value: format,
}));

export const Route = createFileRoute('/_layout/lessons/assessment-contents/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: AssessmentContentsIndex,
  validateSearch: assessmentContentsSearchSchema,
});

const columnHelper = createColumnHelper<AssessmentContent>();
const columns = [
  columnHelper.accessor('name', {
    header: 'Name',
    cell: ({ row, getValue }) => (
      <Link to="/lessons/assessment-contents/$assessmentContentId" params={{ assessmentContentId: row.original.id }}>
        {getValue()}
      </Link>
    ),
  }),
  columnHelper.accessor('format', {
    header: 'Format',
  }),
  columnHelper.accessor('words', {
    header: 'Words',
    cell: ({ getValue }) => {
      return <div>{getValue().join(', ')}</div>;
    },
  }),
  columnHelper.accessor('readingPassage', {
    header: 'Reading passage',
    cell: ({ getValue }) => {
      const passage = getValue();
      if (!passage) {
        return '';
      }
      return <div className="max-w-md whitespace-pre-wrap break-words">{passage}</div>;
    },
  }),
  columnHelper.accessor('requireStudentGrades', {
    header: 'Require student grades',
    cell: ({ getValue }) => getValue()?.join(', ') ?? '',
  }),
  columnHelper.accessor('maxErrorsAllowed', {
    header: 'Max errors allowed',
  }),
  columnHelper.accessor('internalOrder', {
    header: 'Internal order',
  }),
  // columnHelper.accessor((row) => row, {
  //   id: 'actions',
  //   header: '',
  //   cell: ({ getValue }) => (
  //     <>
  //       <CreateEditAssessmentContent
  //         trigger={
  //           <Button variant={'outline'} size={'sm'}>
  //             Edit
  //           </Button>
  //         }
  //         key={getValue().id}
  //         from={getValue()}
  //       />
  //     </>
  //   ),
  // }),
];

function AssessmentContentsIndex() {
  const [[query, activeQuery], setQuery] = useDebouncedSearchState(Route.id, 'query');
  const [[selectedFormats, activeSelectedFormats], setSelectedFormats] = useDebouncedSearchState(Route.id, 'formats');
  const { data: assessmentContents = [], isPending, error, refetch } = useQuery(getAssessmentContents());

  useErrorToast(error, 'Error loading assessment contents. Please try again.');

  // This page does not paginate, so frontend filtering is ok.
  const filteredAssessmentContents = useMemo(() => {
    const normalizedQuery = activeQuery?.trim().toLowerCase();
    const formats = activeSelectedFormats ?? [];

    return assessmentContents.filter((content) => {
      if (normalizedQuery && !content.name.toLowerCase().includes(normalizedQuery)) {
        return false;
      }
      if (formats.length > 0 && !formats.includes(content.format)) {
        return false;
      }
      return true;
    });
  }, [assessmentContents, activeQuery, activeSelectedFormats]);

  const resetFilters = useCallback(() => {
    setQuery(undefined);
    setSelectedFormats(undefined);
  }, [setQuery, setSelectedFormats]);

  const table = useReactTable({
    data: filteredAssessmentContents,
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      enableSorting: false,
    },
  });

  return (
    <>
      <InfoPage
        headerText="Assessment contents"
        headerActions={
          <>
            <CreateAssessmentContentsByCsv trigger={<Button>Create assessment contents by CSV</Button>} />
          </>
        }
        tabs={lessonsSectionTabs()}
      >
        <EntityTable
          table={table}
          isLoading={isPending}
          hasError={!!error}
          emptyState={'No assessment contents found'}
          errorState={<TableErrorState refetchAction={refetch} />}
          filters={{
            filterLeftComponents: (
              <>
                <TableFilterInputField
                  placeholder="Search by content name"
                  value={query ?? ''}
                  onChange={(event) => {
                    setQuery(event.target.value);
                  }}
                />
                <TableFilterDropdown
                  title="Format"
                  options={assessmentFormatFilterOptions}
                  selectedValues={selectedFormats ?? []}
                  onChange={setSelectedFormats}
                  className="w-[400px]"
                />
                <TableFilterResetButton show={true} onClick={resetFilters} />
              </>
            ),
          }}
        />
      </InfoPage>
    </>
  );
}
